/**
 * 말발굽. 말 하나하나에 소리를 달지 않는다 — **달리는 말의 수와 걸음**을 틱마다 세어(`HoofWatch`) 그만큼의 발굽 소리를
 * 한 겹으로 낸다(`HoofMix`). 기병 백이 달려도 소리는 몇 개다.
 *
 * `HoofWatch`는 판을 읽기만 한다(자리·속도·병종). 판정은 건드리지 않는다 — 타격의 셈(`World.fx`)과 달리 판 쪽에
 * 더한 것이 없다. 말 탄 사람은 기병 병종이고 말에서 내리지 않은 사람이다(화면이 말을 그리는 조건과 같다, `render/looks.ts`).
 *
 * 밤에는 **우리 말만** 센다. 보이지 않는 적 기병이 몇이나 어디서 달리는지 소리로 드러나면 안 된다(타격의 좌우·거리를
 * 밤에 뺀 것과 같은 까닭, `battle-mix.ts`).
 */
import { BranchCode } from '../sim/troops.ts';
import { Team } from '../sim/units.ts';
import type { World } from '../sim/world.ts';
import type { GrainReq, Hear, Hearing } from './battle-mix.ts';

/** 이번 틱의 말: 움직인 수, 걸음(0~1, 제 병종 걸음에 견준 몫의 평균), 그 한가운데(전장 px). */
export interface Herd {
  n: number;
  pace: number;
  x: number;
  y: number;
}

/** 이보다 느리면 선 것으로 본다(1/256 px/틱). 싸우며 밀고 밀리는 떨림은 발굽 소리가 아니다. */
export const HOOF_MIN_SPEED = 90;

export class HoofWatch {
  private mounted = new Uint8Array(0);
  private seen = 0;
  private readonly ownOnly: boolean;

  constructor(ownOnly = false) {
    this.ownOnly = ownOnly;
  }

  /** 판을 읽어 `out`에 적는다. 유닛이 늘면(증원) 말 탄 사람의 표를 다시 만든다. */
  read(w: World, out: Herd): void {
    if (this.seen !== w.count) {
      if (this.mounted.length < w.count) this.mounted = new Uint8Array(w.count);
      for (let i = 0; i < w.count; i++) {
        const spec = w.specs[w.specIdx[i]];
        this.mounted[i] = spec.branch === BranchCode.Horse && spec.dismounted !== true ? 1 : 0;
      }
      this.seen = w.count;
    }
    let n = 0;
    let pace = 0;
    let x = 0;
    let y = 0;
    for (let i = 0; i < w.count; i++) {
      if (this.mounted[i] === 0 || w.alive[i] !== 1 || w.exited[i] === 1) continue;
      if (this.ownOnly && w.team[i] !== Team.Player) continue;
      const vx = w.velX[i];
      const vy = w.velY[i];
      const v2 = vx * vx + vy * vy;
      if (v2 < HOOF_MIN_SPEED * HOOF_MIN_SPEED) continue;
      const full = w.sSpeed[i] > 0 ? w.sSpeed[i] : 1;
      pace += Math.min(1, Math.sqrt(v2) / full);
      x += w.x[i] >> 8;
      y += w.y[i] >> 8;
      n++;
    }
    out.n = n;
    out.pace = n > 0 ? pace / n : 0;
    out.x = n > 0 ? x / n : 0;
    out.y = n > 0 ? y / n : 0;
  }
}

/** 발굽을 내는 방식. `gallop`: 말 한두 필의 "다그닥"이 또렷하다. `scatter`: 발굽이 고르게 흩어진다(무리). `both`: 둘 다. */
export type HoofPattern = 'gallop' | 'scatter' | 'both';

export interface HoofSettings {
  pattern: HoofPattern;
  /** 발굽의 잦기 배율. */
  rate: number;
  /** 크기 배율. */
  gain: number;
}

/**
 * 플레이한 사람이 고른 것(2026-10-08): 다그닥 위에 흩어진 발굽("둘 다"), 잦기와 크기는 보통. 한 필·열두 필의 돌격과
 * 실제 판의 흐름을 틀어 보는 쪽에서, 전투 소리(칼·창·화살·깔개)와 함께 들어 보고 골랐다. 발 하나의 소리는 흙을 딛는
 * 둔탁한 소리다(`grains.ts`의 `hoofEarth`).
 */
export const HOOF_DEFAULT: HoofSettings = { pattern: 'both', rate: 1, gain: 1 };

/** 이 수의 말이 달리면 가장 크다. 그 위로는 더 커지지 않는다. */
export const HERD_FULL = 12;
/** 한 틱에 낼 수 있는 발굽. 타격의 상한(`BattleMix`)과 따로 센다 — 발굽이 칼 소리의 자리를 먹지 않는다. */
export const HOOF_PER_TICK = 2;
/** 다그닥 한 바퀴의 길이(틱) — 온 걸음으로 달릴 때. 느리면 길어진다. */
const GALLOP_TICKS = 13;
/** 다그닥의 세 발: 바퀴 안의 자리(0~1)와 크기. 마지막 발이 가장 세다. */
const GALLOP_BEATS: readonly (readonly [number, number])[] = [[0, 0.7], [0.22, 0.8], [0.44, 1]];
const TICK_MS = 1000 / 30;

interface Voice {
  /** 이 말의 바퀴 안 자리(틱). */
  t: number;
  /** 바퀴 길이를 조금 달리한다 — 말들이 한 박자로 뛰지 않게. */
  slow: number;
}

export class HoofMix {
  settings: HoofSettings;
  /** 자리를 싣지 않는다(밤). */
  flat = false;
  private acc = 0;
  private readonly voices: Voice[] = [
    { t: 0, slow: 0 },
    { t: 5, slow: 1 },
    { t: 9, slow: 2 },
  ];
  private readonly out: GrainReq[] = [];
  private readonly at: Hearing = { pan: 0, dist: 0 };
  private readonly rng: () => number;

  constructor(settings: HoofSettings = HOOF_DEFAULT, rng: () => number = Math.random) {
    this.settings = settings;
    this.rng = rng;
  }

  /** 한 틱의 말 무리를 발굽 소리로 바꾼다. 돌려준 배열은 다음 부름에 다시 쓴다. */
  step(herd: Herd, hear: Hear | null, speed = 1): readonly GrainReq[] {
    const out = this.out;
    out.length = 0;
    const s = this.settings;
    // 걷는 말은 조용하다. 뛸 때부터 들린다.
    if (herd.n === 0 || herd.pace < 0.2) {
      this.acc = 0;
      return out;
    }
    const size = Math.min(1, herd.n / HERD_FULL);
    let pan = 0;
    let near = 1;
    if (this.flat) near = 0.6;
    else if (hear) {
      hear(herd.x, herd.y, this.at);
      pan = this.at.pan * 0.8;
      near = Math.max(0.25, 1 - (this.at.dist / 900) * 0.75);
    }
    const loud = s.gain * near * (0.45 + 0.55 * herd.pace) * (0.55 + 0.45 * size);
    const push = (g: number): void => {
      if (out.length >= HOOF_PER_TICK) return;
      out.push({
        kind: 'hoof',
        gain: Math.min(1, loud * g),
        pan: this.flat ? 0 : Math.max(-0.8, Math.min(0.8, pan + (this.rng() - 0.5) * 0.4)),
        delayMs: this.rng() * TICK_MS * 0.6,
        pitch: 0.85 + this.rng() * 0.3,
      });
    };
    if (s.pattern !== 'scatter') {
      // 다그닥: 말이 많을수록 겹쳐 뛰는 목소리가 는다(하나 → 둘 → 셋).
      const voices = herd.n > 12 ? 3 : herd.n > 4 ? 2 : 1;
      for (let v = 0; v < voices; v++) {
        const voice = this.voices[v];
        const cycle = Math.max(6, Math.round(((GALLOP_TICKS + voice.slow) * speed) / (Math.max(0.45, herd.pace) * s.rate)));
        for (const [frac, g] of GALLOP_BEATS) if (voice.t === Math.round(frac * cycle)) push(g * (v === 0 ? 1 : 0.75));
        voice.t = voice.t + 1 >= cycle ? 0 : voice.t + 1;
      }
    }
    if (s.pattern !== 'gallop') {
      // 흩어진 발굽: 1초에 (1.5 + 6×무리의 크기)×걸음 번쯤. 틱마다의 몫을 모아 한 번을 넘으면 낸다.
      this.acc += ((s.pattern === 'both' ? 0.6 : 1) * s.rate * (1.5 + 6 * size) * herd.pace) / (30 * Math.max(1, speed));
      while (this.acc >= 1) {
        this.acc -= 1;
        push(0.7);
      }
    }
    return out;
  }
}
